import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import MapComponent from '../components/GoogleMapComponent';
import LanguageSwitcher from '../components/LanguageSwitcher';
import PayPalPayment from '../components/PayPalPayment';
import RideReceipt from '../components/RideReceipt';
import { mockLocations } from '../mock';
import { requestRide, getRideHistory, rateRide, getActiveRide } from '../services/api';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { 
  Car, MapPin, Clock, Star, User, History, 
  LogOut, Home, CreditCard, MessageSquare, ArrowRight,
  Search, Navigation, Wallet, FileText
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const PassengerDashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isArabic = i18n.language === 'ar';
  
  const [currentView, setCurrentView] = useState('home'); // home, booking, tracking, history, profile
  const [pickupLocation, setPickupLocation] = useState(null);
  const [dropoffLocation, setDropoffLocation] = useState(null);
  const [selectedCarType, setSelectedCarType] = useState(null);
  const [currentRide, setCurrentRide] = useState(null);
  const [rideStatus, setRideStatus] = useState('idle'); // idle, searching, arriving, inProgress, completed
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [rating, setRating] = useState(0);
  const [creditBalance, setCreditBalance] = useState(0);
  const [rideHistory, setRideHistory] = useState([]);
  const [receiptRideId, setReceiptRideId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [farePerKm] = useState(6500); // 6500 SYP per KM
  
  const fetchCreditBalance = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/credits/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreditBalance(response.data.balance);
    } catch (error) {
      console.error('Failed to fetch credit balance:', error);
    }
  }, []);
  
  const fetchRideHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getRideHistory();
      setRideHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch ride history:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCreditBalance();
    if (currentView === 'history') {
      fetchRideHistory();
    }
  }, [currentView, fetchCreditBalance, fetchRideHistory]);
  
  const handleBookRide = () => {
    if (!pickupLocation || !dropoffLocation) {
      alert(isArabic ? 'الرجاء تحديد مواقع الانطلاق والوصول' : 'Please select pickup and dropoff locations');
      return;
    }
    setCurrentView('booking');
  };
  
  const handleConfirmRide = async (carType) => {
    setSelectedCarType(carType);
    setLoading(true);
    
    try {
      const rideData = {
        pickupLocation: {
          address: pickupLocation.name || pickupLocation.nameAr,
          lat: pickupLocation.lat,
          lng: pickupLocation.lng
        },
        dropoffLocation: {
          address: dropoffLocation.name || dropoffLocation.nameAr,
          lat: dropoffLocation.lat,
          lng: dropoffLocation.lng
        },
        carType: carType,
        paymentMethod: 'cash' // Using credits
      };
      
      const response = await requestRide(rideData);
      
      toast({
        title: isArabic ? 'تم حجز الرحلة' : 'Ride Booked',
        description: isArabic ? 'جاري البحث عن سائق...' : 'Looking for a driver...'
      });
      
      setCurrentRide(response.data);
      setCurrentView('tracking');
      setRideStatus('searching');
      
      // Simulate finding driver
      setTimeout(() => {
        setRideStatus('arriving');
      }, 2000);
      
      // Refresh credit balance
      fetchCreditBalance();
      
    } catch (error) {
      toast({
        title: isArabic ? 'خطأ' : 'Error',
        description: error.response?.data?.detail || 'Failed to book ride',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleStartRide = () => {
    setRideStatus('inProgress');
  };
  
  const handleCompleteRide = () => {
    setRideStatus('completed');
    setShowPaymentDialog(true);
  };
  
  const handlePaymentSuccess = () => {
    setShowPaymentDialog(false);
    setShowRatingDialog(true);
  };
  
  const handleSubmitRating = async () => {
    if (rating > 0 && currentRide) {
      try {
        await rateRide(currentRide.id, { rating });
      } catch (error) {
        console.error('Failed to submit rating:', error);
      }
    }
    
    setShowRatingDialog(false);
    setCurrentView('home');
    setRideStatus('idle');
    setPickupLocation(null);
    setDropoffLocation(null);
    setSelectedCarType(null);
    setCurrentRide(null);
    setRating(0);
    fetchCreditBalance();
  };
  
  const renderHome = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-black to-black rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold mb-2">{t('whereAreYouGoing')}</h2>
            <p className="text-black">{isArabic ? 'اختر وجهتك واحجز رحلتك الآن' : 'Select your destination and book your ride now'}</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              <div>
                <p className="text-xs text-black">{isArabic ? 'رصيدك' : 'Your Balance'}</p>
                <p className="text-xl font-bold">{creditBalance.toLocaleString()} {isArabic ? 'ل.س' : 'SYP'}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white/10 rounded-lg p-3 text-sm">
          <p>{isArabic ? `💰 السعر: ${farePerKm.toLocaleString()} ل.س لكل كيلومتر` : `💰 Rate: ${farePerKm.toLocaleString()} SYP per kilometer`}</p>
        </div>
      </div>
      
      <Card className="p-6 space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block flex items-center gap-2">
            <MapPin className="h-4 w-4 text-green-600" />
            {t('pickup')}
          </label>
          <Select onValueChange={(value) => setPickupLocation(mockLocations[parseInt(value)])}>
            <SelectTrigger>
              <SelectValue placeholder={isArabic ? 'اختر موقع الانطلاق' : 'Select pickup location'} />
            </SelectTrigger>
            <SelectContent>
              {mockLocations.map((loc, idx) => (
                <SelectItem key={loc.name} value={idx.toString()}>
                  {isArabic ? loc.nameAr : loc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <label className="text-sm font-medium mb-2 block flex items-center gap-2">
            <MapPin className="h-4 w-4 text-red-600" />
            {t('dropoff')}
          </label>
          <Select onValueChange={(value) => setDropoffLocation(mockLocations[parseInt(value)])}>
            <SelectTrigger>
              <SelectValue placeholder={isArabic ? 'اختر موقع الوصول' : 'Select dropoff location'} />
            </SelectTrigger>
            <SelectContent>
              {mockLocations.map((loc, idx) => (
                <SelectItem key={loc.name} value={idx.toString()}>
                  {isArabic ? loc.nameAr : loc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <Button 
          className="w-full bg-gradient-to-r from-black to-black hover:from-black hover:to-black" 
          size="lg"
          onClick={handleBookRide}
        >
          <Search className="mr-2 h-5 w-5" />
          {t('findRide')}
        </Button>
      </Card>
      
      {pickupLocation && (
        <div className="rounded-xl overflow-hidden shadow-lg">
          <MapComponent 
            center={[pickupLocation.lat, pickupLocation.lng]}
            pickup={pickupLocation}
            dropoff={dropoffLocation}
            showRoute={!!dropoffLocation}
            height="300px"
          />
        </div>
      )}
    </div>
  );
  
  const renderBooking = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => setCurrentView('home')}>
          <ArrowRight className={`h-5 w-5 ${isArabic ? '' : 'rotate-180'}`} />
          {t('back')}
        </Button>
        <h2 className="text-2xl font-bold">{t('selectCar')}</h2>
        <div className="w-20"></div>
      </div>
      
      <div className="rounded-xl overflow-hidden shadow-lg">
        <MapComponent 
          center={[pickupLocation.lat, pickupLocation.lng]}
          pickup={pickupLocation}
          dropoff={dropoffLocation}
          showRoute={true}
          height="250px"
        />
      </div>
      
      <div className="space-y-3">
        {['economy', 'comfort', 'premium'].map((carType) => {
          const estimatedDistance = 5; // km
          const fare = estimatedDistance * farePerKm;
          const carNames = {
            economy: { en: 'Economy', ar: 'اقتصادي' },
            comfort: { en: 'Comfort', ar: 'مريح' },
            premium: { en: 'Premium', ar: 'فاخر' }
          };
          const carIcons = {
            economy: '🚗',
            comfort: '🚙',
            premium: '🚘'
          };
          
          return (
            <Card 
              key={carType} 
              className="p-4 cursor-pointer hover:shadow-lg transition-all hover:border-green-500 border-2"
              onClick={() => handleConfirmRide(carType)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="text-4xl">{carIcons[carType]}</div>
                  <div>
                    <h3 className="font-semibold text-lg">{isArabic ? carNames[carType].ar : carNames[carType].en}</h3>
                    <p className="text-sm text-gray-600">{isArabic ? `${estimatedDistance} كم × ${farePerKm.toLocaleString()} ل.س` : `${estimatedDistance} km × ${farePerKm.toLocaleString()} SYP`}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">3-5 min</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-black">
                    {fare.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-500">{isArabic ? 'ل.س' : 'SYP'}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
        <p>{isArabic ? `⚠️ رصيدك الحالي: ${creditBalance.toLocaleString()} ل.س` : `⚠️ Your current balance: ${creditBalance.toLocaleString()} SYP`}</p>
      </div>
    </div>
  );
  
  const renderTracking = () => (
    <div className="space-y-6">
      <div className="rounded-xl overflow-hidden shadow-lg">
        <MapComponent 
          center={[pickupLocation.lat, pickupLocation.lng]}
          pickup={pickupLocation}
          dropoff={dropoffLocation}
          driverLocation={[pickupLocation.lat + 0.005, pickupLocation.lng + 0.005]}
          showRoute={true}
          height="400px"
        />
      </div>
      
      {rideStatus === 'searching' && (
        <Card className="p-6 text-center">
          <div className="animate-pulse">
            <Car className="h-12 w-12 mx-auto mb-4 text-black" />
            <h3 className="text-xl font-semibold mb-2">{t('waitingForDriver')}</h3>
            <p className="text-gray-600">{isArabic ? 'جاري البحث عن أقرب سائق...' : 'Finding nearest driver...'}</p>
          </div>
        </Card>
      )}
      
      {rideStatus === 'arriving' && currentRide && (
        <Card className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-16 w-16">
                <AvatarImage src={`https://ui-avatars.com/api/?name=${encodeURIComponent(currentRide.driverName || 'Driver')}&background=000000&color=fff`} />
                <AvatarFallback>{(currentRide.driverName || 'D').charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-semibold text-lg">{currentRide.driverName}</h3>
                <p className="text-sm text-gray-600">{currentRide.carModel}</p>
                <p className="text-sm font-medium">{currentRide.carNumber}</p>
                <div className="flex items-center gap-1 mt-1">
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  <span className="text-sm font-medium">{currentRide.rating}</span>
                </div>
              </div>
            </div>
            <Badge className="bg-green-100 text-green-800">{t('driverArriving')}</Badge>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-700">{isArabic ? 'وقت الوصول' : 'Arrival Time'}</span>
              <span className="font-semibold text-lg">{currentRide.eta}</span>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button className="flex-1" variant="outline">
              <MessageSquare className="mr-2 h-4 w-4" />
              {isArabic ? 'مراسلة' : 'Message'}
            </Button>
            <Button className="flex-1 bg-gradient-to-r from-black to-black" onClick={handleStartRide}>
              <Navigation className="mr-2 h-4 w-4" />
              {t('startRide')}
            </Button>
          </div>
        </Card>
      )}
      
      {rideStatus === 'inProgress' && currentRide && (
        <Card className="p-6">
          <Badge className="bg-blue-100 text-blue-800 mb-4">{t('inProgress')}</Badge>
          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-700">{isArabic ? 'الأجرة' : 'Fare'}</span>
              <span className="font-semibold text-lg">{currentRide.fare.toLocaleString()} {isArabic ? 'ل.س' : 'SYP'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700">{isArabic ? 'المسافة' : 'Distance'}</span>
              <span className="font-medium">5.2 km</span>
            </div>
          </div>
          <Button className="w-full bg-gradient-to-r from-green-600 to-emerald-600" onClick={handleCompleteRide}>
            {t('completeRide')}
          </Button>
        </Card>
      )}
    </div>
  );
  
  const renderHistory = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">{t('rideHistory')}</h2>
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
        </div>
      ) : rideHistory.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-gray-500">{isArabic ? 'لا توجد رحلات سابقة' : 'No ride history'}</p>
        </Card>
      ) : (
        rideHistory.map((ride) => (
        <Card key={ride.id} className="p-4 hover:shadow-lg transition-all">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="h-4 w-4 text-green-600" />
                <span className="font-medium">{ride.pickup.address}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-red-600" />
                <span className="font-medium">{ride.dropoff.address}</span>
              </div>
            </div>
            <Badge variant={ride.status === 'completed' ? 'success' : 'default'}>
              {t(ride.status)}
            </Badge>
          </div>
          
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div className="flex items-center gap-4">
              <span>{ride.driverName}</span>
              <span>•</span>
              <span>{ride.distance}</span>
              <span>•</span>
              <span>{ride.duration}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">{ride.fare.toLocaleString()} {isArabic ? 'ل.س' : 'SYP'}</span>
            </div>
          </div>
          
          <div className="flex items-center justify-between mt-3 pt-3 border-t">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star 
                  key={`star-${star}`}
                  className={`h-4 w-4 ${star <= ride.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
                />
              ))}
            </div>
            <div className="flex items-center gap-3">
              {ride.status === 'completed' && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-green-700 hover:text-green-800 hover:bg-green-50"
                  onClick={() => setReceiptRideId(ride.id)}
                  data-testid={`receipt-btn-${ride.id}`}
                >
                  <FileText className="h-4 w-4 mr-1" />
                  {isArabic ? 'الإيصال' : 'Receipt'}
                </Button>
              )}
              <span className="text-xs text-gray-500">
                {new Date(ride.requestedAt || ride.completedAt).toLocaleDateString(isArabic ? 'ar-SY' : 'en-US')}
              </span>
            </div>
          </div>
        </Card>
      )))}
      <RideReceipt
        rideId={receiptRideId}
        open={!!receiptRideId}
        onClose={() => setReceiptRideId(null)}
        isArabic={isArabic}
      />
    </div>
  );
  
  const renderProfile = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <Avatar className="h-20 w-20">
            <AvatarImage src={mockUser.profileImage} />
            <AvatarFallback>{(mockUser?.name || 'U').charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div>
            <h2 className="text-2xl font-bold">{mockUser.name}</h2>
            <p className="text-gray-600">{mockUser.email}</p>
            <p className="text-gray-600">{mockUser.phone}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <Star className="h-6 w-6 mx-auto mb-2 text-black" />
            <p className="text-2xl font-bold text-black">{mockUser.rating}</p>
            <p className="text-sm text-gray-600">{t('rating')}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <Car className="h-6 w-6 mx-auto mb-2 text-black" />
            <p className="text-2xl font-bold text-black">{mockUser.totalRides}</p>
            <p className="text-sm text-gray-600">{t('totalRides')}</p>
          </div>
        </div>
      </Card>
      
      <Card className="p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          {t('paymentMethod')}
        </h3>
        <div className="space-y-2">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span>{t('cash')}</span>
            <Badge>Default</Badge>
          </div>
        </div>
      </Card>
    </div>
  );
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/saeed-logo.png" alt="Saeed" className="h-10 w-10 object-contain bg-black rounded-lg p-1" />
            <h1 className="text-xl font-bold tracking-wide">SAEED</h1>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {currentView === 'home' && renderHome()}
        {currentView === 'booking' && renderBooking()}
        {currentView === 'tracking' && renderTracking()}
        {currentView === 'history' && renderHistory()}
        {currentView === 'profile' && renderProfile()}
      </main>
      
      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t">
        <div className="max-w-4xl mx-auto px-4 py-3 flex justify-around">
          <Button 
            variant="ghost" 
            className={currentView === 'home' ? 'text-black' : 'text-gray-600'}
            onClick={() => setCurrentView('home')}
          >
            <div className="flex flex-col items-center gap-1">
              <Home className="h-5 w-5" />
              <span className="text-xs">{isArabic ? 'الرئيسية' : 'Home'}</span>
            </div>
          </Button>
          <Button 
            variant="ghost" 
            className={currentView === 'history' ? 'text-black' : 'text-gray-600'}
            onClick={() => setCurrentView('history')}
          >
            <div className="flex flex-col items-center gap-1">
              <History className="h-5 w-5" />
              <span className="text-xs">{isArabic ? 'السجل' : 'History'}</span>
            </div>
          </Button>
          <Button 
            variant="ghost" 
            className={currentView === 'profile' ? 'text-black' : 'text-gray-600'}
            onClick={() => setCurrentView('profile')}
          >
            <div className="flex flex-col items-center gap-1">
              <User className="h-5 w-5" />
              <span className="text-xs">{isArabic ? 'الملف' : 'Profile'}</span>
            </div>
          </Button>
        </div>
      </nav>
      
      {/* Payment Dialog */}
      {currentRide && (
        <PayPalPayment
          open={showPaymentDialog}
          onClose={() => setShowPaymentDialog(false)}
          rideFare={currentRide.fare}
          onSuccess={handlePaymentSuccess}
        />
      )}
      
      {/* Rating Dialog */}
      <Dialog open={showRatingDialog} onOpenChange={setShowRatingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('rateDriver')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className="transition-transform hover:scale-110"
                >
                  <Star 
                    className={`h-10 w-10 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                  />
                </button>
              ))}
            </div>
            <Button 
              className="w-full bg-gradient-to-r from-black to-black"
              onClick={handleSubmitRating}
              disabled={rating === 0}
            >
              {t('submit')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PassengerDashboard;